import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CalendarioService } from '@services/calendario/calendario.service';
import { EventoCalendario } from '@interfaces/calendario/calendario-interface';
import Swal from 'sweetalert2';
import { GuiMsjService } from '@services/shared/gui-msj.service';
import { MapComponent } from '@components/entidades/common/map/map.component';
import * as _moment from 'moment';

@Component({
  selector: 'app-eventos-calendario',
  templateUrl: './eventos-calendario.component.html',
  styleUrls: ['./eventos-calendario.component.scss']
})
export class EventosCalendarioComponent implements OnInit, AfterViewInit {

  form!: FormGroup;
  @ViewChild(MapComponent) mapComponent!: MapComponent;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  eventoId: string | null = null;

  constructor(
    private calendarioService: CalendarioService,
    private router: Router,
    private route: ActivatedRoute,
    private guiMsjService: GuiMsjService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.isEditMode = true;
        this.eventoId = id;
        this.cargarEvento(id);
      } else {
        this.isEditMode = false;
        this.eventoId = null;
        this.resetForm();
      }
    });
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.updateMap();
  }

  createForm() {
    this.form = new FormGroup({
      titulo: new FormControl('', Validators.required),
      descripcion: new FormControl('', Validators.required),
      fechaInicio: new FormControl(null, Validators.required),
      fechaFin: new FormControl(null, Validators.required),
      ubicacion: new FormControl('', Validators.required),
      color: new FormControl('#BC4B51', Validators.required),
      latitud: new FormControl(-34.63104776379445, [
        Validators.required,
        Validators.pattern(/^-?\d*(\.\d+)?$/)
      ]),
      longitud: new FormControl(-58.46651124133983, [
        Validators.required,
        Validators.pattern(/^-?\d*(\.\d+)?$/)
      ]),
      links: new FormArray([]),
    });
  }

  resetForm() {
    this.linksFormArray.clear();
    this.form.patchValue({
      titulo: '',
      descripcion: '',
      fechaInicio: null,
      fechaFin: null,
      ubicacion: '',
      color: '#BC4B51',
      latitud: -34.63104776379445,
      longitud: -58.46651124133983
    });
    this.updateMarkerLocation();
  }

  async cargarEvento(id: string) {
    this.isLoading = true;
    try {
      const evento = await this.calendarioService.obtenerEventoPorId(id);
      if (evento) {
        this.form.patchValue({
          titulo: evento.titulo,
          descripcion: evento.descripcion,
          ubicacion: evento.ubicacion,
          color: evento.color,
          latitud: evento.latitud,
          longitud: evento.longitud
        });

        if (evento.fechaInicio) {
          const fechaInicio = new Date(evento.fechaInicio);
          this.form.get('fechaInicio')?.setValue(fechaInicio);
          this.form.get('fechaInicio')?.updateValueAndValidity();
        }
        if (evento.fechaFin) {
          const fechaFin = new Date(evento.fechaFin);
          this.form.get('fechaFin')?.setValue(fechaFin);
          this.form.get('fechaFin')?.updateValueAndValidity();
        }

        this.linksFormArray.clear();
        if (evento.links && evento.links.length > 0) {
          evento.links.forEach(link => {
            const group = this.createLinkGroup();
            group.patchValue({
              label: link.label || '',
              link: link.link || ''
            });
            this.linksFormArray.push(group);
          });
        }

        this.updateMarkerLocation();
        setTimeout(() => this.cdr.detectChanges(), 0);
      } else {
        Swal.fire('Error', 'No se encontró el evento a editar.', 'error');
        this.router.navigate(['/configuracion-calendario']);
      }
    } catch (error) {
      console.error('Error al cargar evento:', error);
      Swal.fire('Error', 'Ocurrió un error al cargar el evento.', 'error');
      this.router.navigate(['/configuracion-calendario']);
    } finally {
      this.isLoading = false;
    }
  }

  async guardarEvento() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.form.disable({ emitEvent: false });
    this.cdr.detectChanges();

    try {
      const eventoData = await this.setForm();
      let response;
      if (this.isEditMode && this.eventoId) {
        response = await this.calendarioService.updateEvento(this.eventoId, eventoData);
      } else {
        response = await this.calendarioService.postEventoCalendario(eventoData);
      }

      if (response !== false && response._id) {
        const eventoId = response._id;
        console.log('Evento guardado, ID:', eventoId);
        await this.guiMsjService.msjFormSubmit(this.isEditMode ? 'Evento actualizado' : 'Evento guardado');
        this.router.navigateByUrl('/configuracion-calendario');
      } else {
        Swal.fire('Error', this.isEditMode ? 'No se pudo actualizar el evento.' : 'No se pudo guardar el evento.', 'error');
      }
    } catch (error) {
      console.error('Error en el proceso:', error);
      Swal.fire('Error', 'Ocurrió un error inesperado al guardar el evento.', 'error');
    } finally {
      this.isLoading = false;
      this.form.enable({ emitEvent: false });
    }
  }

  private async setForm(): Promise<EventoCalendario> {
    const rawFechaInicio: _moment.Moment = this.form.value.fechaInicio;
    const rawFechaFin: _moment.Moment = this.form.value.fechaFin;

    const fechaInicioParaBackend = rawFechaInicio ? rawFechaInicio.toISOString() : '';
    const fechaFinParaBackend = rawFechaFin ? rawFechaFin.toISOString() : '';

    return {
      titulo: this.form.value.titulo || '',
      descripcion: this.form.value.descripcion || '',
      fechaInicio: fechaInicioParaBackend,
      fechaFin: fechaFinParaBackend,
      ubicacion: this.form.value.ubicacion || '',
      color: this.form.value.color || '',
      latitud: this.form.value.latitud,
      longitud: this.form.value.longitud,
      links: this.linksFormArray.value
    };
  }

  initMap() {
    if (this.mapComponent && typeof this.form.value.latitud === 'number' && typeof this.form.value.longitud === 'number' && !isNaN(this.form.value.latitud) && !isNaN(this.form.value.longitud)) {
      this.mapComponent.getLocation(this.form.value.latitud, this.form.value.longitud);
    }
  }

  updateMap() {
    this.updatesCoordinates();
    if (this.mapComponent && typeof this.form.value.latitud === 'number' && typeof this.form.value.longitud === 'number' && !isNaN(this.form.value.latitud) && !isNaN(this.form.value.longitud)) {
      this.mapComponent.getLocation(this.form.value.latitud, this.form.value.longitud);
    }
  }

  updatesCoordinates() {
    if (this.mapComponent) {
      this.mapComponent.coordinatesChanged.subscribe((coordinates) => {
        this.form.patchValue({
          latitud: coordinates.latitud,
          longitud: coordinates.longitud
        });
      });
    }
  }

  updateMarkerLocation() {
    const latitud = this.form.get('latitud')?.value;
    const longitud = this.form.get('longitud')?.value;
    if (this.mapComponent && latitud !== null && longitud !== null && !isNaN(latitud) && !isNaN(longitud)) {
      this.mapComponent.getLocation(latitud, longitud);
    }
  }

  private createLinkGroup(): FormGroup {
    return new FormGroup({
      label: new FormControl(''),
      link: new FormControl('', [Validators.pattern(/^(ftp|http|https):\/\/[^ "]+$/)])
    });
  }

  get linksFormArray(): FormArray {
    return this.form.get('links') as FormArray;
  }

  addLink(): void {
    this.linksFormArray.push(this.createLinkGroup());
  }

  removeLink(index: number): void {
    this.linksFormArray.removeAt(index);
  }
  
}